import { Popover2 } from '@blueprintjs/popover2'
import { useQuery } from '@tanstack/react-query'
import cx from 'classnames'
import { useState } from 'react'
import useBackendClient from '~/api/BackendClient/useBackendClient'
import type { PlateFilter } from '~/api/backend/pages/culturesPageAPI'
import { QUERY_KEYS } from '~/api/query-client-keys'
import Button from '~/components/buttons/Button'
import MinimalButton from '~/components/buttons/MinimalButton'
import FilterIcon from '~/components/icons/FilterIcon'
import FilterMenu from './FilterMenu'
import FilterPill from './FilterPill'
import IsBetweenDatetimeRangeFilterModal from './IsBetweenDatetimeRangeFilterModal'

function FilterBar(props: {
  className: string
  filters: PlateFilter[]
  setFilters: (filters: PlateFilter[]) => void
  isSelectionMode?: boolean
  onToggleSelectionMode?: () => void
}) {
  const [
    isBetweenDatetimeRangeFilterDialogField,
    setIsBetweenDatetimeRangeFilterDialogField,
  ] = useState<string | null>(null)
  const client = useBackendClient()
  const { data, isLoading } = useQuery({
    queryKey: [QUERY_KEYS.CULTURE_PAGE_FILTER_DEFINITIONS],
    queryFn: async () => await client.culturesPage.getFilterDefinitions(),
  })

  if (isLoading || !data) {
    return (
      <div
        className={cx(
          props.className,
          'flex items-center justify-between border-b border-gray-200 px-10 py-1.5 min-h-[52px]',
        )}
      />
    )
  }

  const renderTarget = ({ ...targetProps }) => (
    <div {...targetProps} className='h-7 flex items-center mr-2.5'>
      <MinimalButton
        label='Filter'
        IconComponent={FilterIcon}
        variant='tiny'
        onClick={() => {}} // Applies cursor: pointer style.
      />
    </div>
  )

  const removeFilterByField = (field: string) => {
    props.setFilters(props.filters.filter(f => f.field !== field))
  }

  const setFilter = (filterField: string, newFilter: PlateFilter | null) => {
    if (newFilter === null) {
      // If newFilter is null, remove the filter
      removeFilterByField(filterField)
      return
    }
    const existingFilterIndex = props.filters.findIndex(f => f.field === filterField)
    if (existingFilterIndex !== -1) {
      // Replace existing filter
      const newFilters = [...props.filters]
      newFilters[existingFilterIndex] = newFilter
      props.setFilters(newFilters)
    } else {
      // Add new filter
      props.setFilters([...props.filters, newFilter])
    }
  }

  const openFilterModal = (filterField: string) => {
    if (
      filterField === 'first_checked_in_at' ||
      filterField === 'last_checked_out_at' ||
      filterField === 'last_generated_at'
    ) {
      setIsBetweenDatetimeRangeFilterDialogField(filterField)
      return
    }
  }

  return (
    <div
      className={cx(
        props.className,
        'flex items-center justify-between border-b border-gray-200 px-10 py-1.5 min-h-[52px]',
      )}
    >
      <div className='flex items-center flex-wrap flex-1 gap-2'>
        <Popover2
          content={
            <FilterMenu
              filterDefinitions={data}
              setFilter={setFilter}
              openFilterModal={openFilterModal}
              filters={props.filters}
            />
          }
          interactionKind='click'
          placement='bottom-start'
          renderTarget={renderTarget}
          minimal
        />
        {props.filters.map(filter => (
          <FilterPill
            filter={filter}
            key={filter.field}
            onDeleteClick={() => removeFilterByField(filter.field)}
          />
        ))}
      </div>
      <div className='w-[400px] flex justify-start items-center'>
        {props.onToggleSelectionMode && (
          <Button
            label='Select plates'
            type='primary'
            onClick={props.onToggleSelectionMode}
          />
        )}
      </div>
      <IsBetweenDatetimeRangeFilterModal
        filterField={isBetweenDatetimeRangeFilterDialogField || ''}
        isOpen={isBetweenDatetimeRangeFilterDialogField !== null}
        onClose={() => setIsBetweenDatetimeRangeFilterDialogField(null)}
        existingFilter={props.filters.find(
          f => f.field === isBetweenDatetimeRangeFilterDialogField,
        )}
        setFilter={setFilter}
      />
    </div>
  )
}

export default FilterBar
